import { Popover as BasePopover } from "@base-ui/react/popover";
import type { ComponentProps, ReactNode } from "react";
import { cn, withBase } from "./cn";

// Paper: 12e notifications, 12g employee card, 13d worker popover. Radius 16, ring plus a
// smaller shadow than dialogs. Rows inside are divided by hairlines, not boxed.

export const Popover = BasePopover.Root;
export const PopoverTrigger = BasePopover.Trigger;
export const PopoverClose = BasePopover.Close;

export type PopoverContentProps = ComponentProps<typeof BasePopover.Popup> &
  Pick<ComponentProps<typeof BasePopover.Positioner>, "side" | "align" | "sideOffset" | "alignOffset"> & {
    width?: number;
  };

export const PopoverContent = ({
  side = "bottom",
  align = "end",
  sideOffset = 8,
  alignOffset = 0,
  width = 380,
  className,
  ...props
}: PopoverContentProps) => (
  <BasePopover.Portal>
    <BasePopover.Positioner
      side={side}
      align={align}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      className="z-50"
      style={{ width }}
    >
      <BasePopover.Popup
        className={withBase(
          "flex max-h-[min(560px,var(--available-height))] w-full origin-(--transform-origin) flex-col overflow-clip rounded-lg bg-surface shadow-popover outline-none transition-[opacity,scale] duration-150 ease-out data-ending-style:scale-[0.98] data-ending-style:opacity-0 data-starting-style:scale-[0.98] data-starting-style:opacity-0",
          className,
        )}
        {...props}
      />
    </BasePopover.Positioner>
  </BasePopover.Portal>
);

export const PopoverHeader = ({ title, actions, className }: { title: ReactNode; actions?: ReactNode; className?: string }) => (
  <div
    className={cn(
      "flex h-12 shrink-0 items-center justify-between pl-4 pr-2 shadow-[inset_0_-1px_0_var(--color-hairline)]",
      className,
    )}
  >
    <BasePopover.Title className="text-[14px] font-medium leading-[18px] text-text">{title}</BasePopover.Title>
    {actions}
  </div>
);

export const PopoverRow = ({ className, ...props }: ComponentProps<"div">) => (
  <div
    className={cn(
      "flex items-center gap-3 px-4 py-3 shadow-[inset_0_-1px_0_var(--color-hairline)] last:shadow-none",
      className,
    )}
    {...props}
  />
);
